import { memo, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { PhoneTree } from '@weave/schema-gen-ts/dist/schemas/phone/tree/v1/phone_tree_service.pb';
import SchemaPhoneTreeService from '@frontend/api-phone-trees';
import { useTranslation } from '@frontend/i18n';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { useQuery } from '@frontend/react-query-helpers';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { DropdownField, Modal, useFormField, useModalControl } from '@frontend/design-system';
import { queryKeys } from '../../../query-keys';
import { useAudioQuery } from '../../../utils/use-audio-query';
import { CanvasPhoneTree, getDialOptionChildren } from '../../call-routes/helpers/graph/node-utils';
import { PhoneTreeOptionRenderer, UnconfiguredReason } from './phone-tree-option-renderer';
import { PhoneTreePreviewModal } from './phone-tree-preview-modal';
import { transformGraphToDialOptions } from './utils';

const getCanvasUnconfiguredReason = (phoneTree: CanvasPhoneTree): UnconfiguredReason | undefined => {
  const hasNoPrompt = !phoneTree.promptMediaItemId;
  const hasNoDialOptions =
    getDialOptionChildren(phoneTree.phoneTreeNodeId, phoneTree.nodes, phoneTree.edges).length === 0;
  if (hasNoPrompt && hasNoDialOptions) return 'prompt-and-dial-options';
  if (hasNoPrompt) return 'prompt';
  if (hasNoDialOptions) return 'dial-options';
  return undefined;
};

const getSavedUnconfiguredReason = (phoneTree: PhoneTree): UnconfiguredReason | undefined => {
  const hasNoPrompt = !phoneTree.prompt?.mediaItemId;
  const hasNoDialOptions = (phoneTree.dialOptionCount ?? 0) === 0;
  if (hasNoPrompt && hasNoDialOptions) return 'prompt-and-dial-options';
  if (hasNoPrompt) return 'prompt';
  if (hasNoDialOptions) return 'dial-options';
  return undefined;
};

// Reusable dropdown component for selecting phone trees
export interface PhoneTreeDropdownProps {
  dropdownProps: ReturnType<typeof useFormField<'dropdown'>>;
  phoneTreesInCallRoute?: CanvasPhoneTree[];
  currentCallRouteId?: string;
  currentCallRouteName?: string;
  currentCallRouteLocationId?: string;
  prioritizedLocationId?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  lastUsedPhoneTreeId?: string;
  locationPhoneTenantId?: string;
  shouldFilterOutNotInCallRoute?: boolean; // whether to filter out phone trees that are not in a call route
  disableUnsavedPhoneTrees?: boolean;
  // Whether to disable phone trees that are unconfigured (missing prompt or missing dial options)
  disableUnconfiguredPhoneTrees?: boolean;
  onSelect?: (selectedPhoneTree: CanvasPhoneTree | (PhoneTree & { onCanvas: false }) | undefined) => void;
}

export const PhoneTreeDropdown = ({
  dropdownProps,
  phoneTreesInCallRoute,
  currentCallRouteId,
  currentCallRouteName,
  currentCallRouteLocationId,
  prioritizedLocationId,
  locationPhoneTenantId,
  name = 'phoneTreeToDuplicate',
  label,
  placeholder,
  lastUsedPhoneTreeId,
  shouldFilterOutNotInCallRoute,
  disableUnsavedPhoneTrees = false,
  disableUnconfiguredPhoneTrees = false,
  onSelect,
  ...rest
}: PhoneTreeDropdownProps) => {
  const { t } = useTranslation('phone');
  const { getScopeName, isSingleTypeScope } = useAppScopeStore();
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');

  const notInCallRouteText = t('Not in Call Routes');
  const mostRecentlyUsedText = t('Most Recently Used');

  const phoneTenantId = locationPhoneTenantId || settingsTenantLocation?.phoneTenantId;

  // Fetch saved phone trees
  const { data: savedPhoneTrees = [] } = useQuery({
    queryKey: queryKeys.phoneTrees(phoneTenantId),
    queryFn: () =>
      SchemaPhoneTreeService.List(
        { tenantId: phoneTenantId || '' },
        { params: { 'includes.count_subsequent_phone_trees': true } }
      ),
    select: (data) => {
      const phoneData = data?.phoneTrees;
      if (!phoneData) {
        return [];
      }

      return phoneData.sort((a: PhoneTree, b: PhoneTree) => (!a.name || !b.name ? 0 : a.name.localeCompare(b.name)));
    },
    enabled: !!phoneTenantId,
  });

  const [phoneTreeToPreview, setPhoneTreeToPreview] = useState<CanvasPhoneTree | PhoneTree | null>(null);
  const { modalProps: previewPhoneTreeModalProps, triggerProps: previewPhoneTreeTriggerProps } = useModalControl();

  useEffect(() => {
    {
      // Reset the selected phone tree to preview when the modal is closed
      if (!previewPhoneTreeModalProps.show) {
        setPhoneTreeToPreview(null);
      }
    }
  }, [previewPhoneTreeModalProps.show]);

  // Fetch audio files
  const { data: audioFiles } = useAudioQuery({
    tenantId: phoneTenantId ?? '',
    allowedOptions: { custom: true, standard: true, mailbox: false },
  });

  // Find the last used phone tree from either source
  const lastUsedPhoneTree = useMemo(() => {
    if (!lastUsedPhoneTreeId) return null;

    // Check in phoneTreesInCallRoute first
    // NOTE: This won't match any phone trees that have not been saved yet since those won't have a primitiveId. This
    // is ok since if the phone tree hasn't been saved yet, then there is no way it could have been the "most recently
    // used"
    const fromCallRoute = phoneTreesInCallRoute?.find((tree) => tree.callObject?.primitiveId === lastUsedPhoneTreeId);
    if (fromCallRoute) {
      return { tree: fromCallRoute, source: 'callRoute' as const };
    }

    // Check in savedPhoneTrees
    const fromSaved = savedPhoneTrees.find((tree) => tree.id === lastUsedPhoneTreeId);
    if (fromSaved) {
      return { tree: { ...fromSaved, onCanvas: false as const }, source: 'saved' as const };
    }

    return null;
  }, [lastUsedPhoneTreeId, phoneTreesInCallRoute, savedPhoneTrees]);

  // Filter out the last used phone tree from phoneTreesInCallRoute
  const filteredPhoneTreesInCallRoute = useMemo(() => {
    if (!lastUsedPhoneTree || lastUsedPhoneTree.source !== 'callRoute') {
      return phoneTreesInCallRoute;
    }
    return phoneTreesInCallRoute?.filter((tree) => tree.callObject?.primitiveId !== lastUsedPhoneTreeId);
  }, [phoneTreesInCallRoute, lastUsedPhoneTree, lastUsedPhoneTreeId]);

  const phoneTreesByLocationId = useMemo(
    () =>
      savedPhoneTrees.reduce<
        Record<string, { locationName: string; locationId: string; trees: (PhoneTree & { onCanvas: false })[] }>
      >((acc, tree) => {
        if (!!tree.callRouteData?.id && tree.callRouteData?.id === currentCallRouteId) {
          // Already on canvas, skip (canvasPhoneTrees is the source of truth for this category)
          return acc;
        }

        // Skip the last used phone tree if it came from saved trees (it will be shown in its own group)
        if (lastUsedPhoneTree?.source === 'saved' && tree.id === lastUsedPhoneTreeId) {
          return acc;
        }

        const rawLocId = tree.callRouteData?.locationId;
        let locId: string;
        if (!rawLocId) {
          // No call route location — tree is not in any call route
          locId = 'unknown';
        } else if (!getScopeName(rawLocId)) {
          // Location is outside the current scope (e.g. multi-location user with limited access to a subset of
          // locations that doesn't include the location of this phone tree's call route)
          locId = 'other-location';
        } else {
          locId = rawLocId;
        }
        if (!acc[locId]) {
          acc[locId] = {
            locationId: locId,
            locationName:
              locId === 'other-location' ? t('Other Phone Trees') : getScopeName(locId) || notInCallRouteText,
            trees: [{ ...tree, onCanvas: false }],
          };

          return acc;
        }

        acc[locId].trees.push({ ...tree, onCanvas: false });
        return acc;
      }, {}),
    [savedPhoneTrees, currentCallRouteId, lastUsedPhoneTree, lastUsedPhoneTreeId, getScopeName, notInCallRouteText]
  );

  const allPhoneTrees = useMemo(() => {
    const allSavedPhoneTrees = Object.values(phoneTreesByLocationId).flatMap((group) => group.trees);
    const combinedTrees = [...(phoneTreesInCallRoute ?? []), ...allSavedPhoneTrees];
    // Include the last used phone tree in allPhoneTrees for selection lookup
    if (lastUsedPhoneTree) {
      return [lastUsedPhoneTree.tree, ...combinedTrees];
    }
    return combinedTrees;
  }, [phoneTreesInCallRoute, phoneTreesByLocationId, lastUsedPhoneTree]);

  const sortedPhoneTreesByLocation = useMemo(
    () =>
      Object.values(phoneTreesByLocationId)
        .filter(({ locationName }) => !shouldFilterOutNotInCallRoute || locationName !== notInCallRouteText)
        .sort((a, b) => {
          // Prioritized location always comes first, "Not in Call Routes" always comes last
          if (a.locationId === prioritizedLocationId) return -1;
          if (b.locationId === prioritizedLocationId) return 1;
          if (a.locationId === 'unknown') return 1;
          if (b.locationId === 'unknown') return -1;
          return a.locationName.localeCompare(b.locationName);
        }),
    [phoneTreesByLocationId, notInCallRouteText, prioritizedLocationId, shouldFilterOutNotInCallRoute]
  );

  // Call onSelect when the dropdown value changes
  const handleChange = (e: { name: string; value: string }) => {
    dropdownProps.onChange(e);
    const newValue = e.value;
    if (onSelect) {
      const selectedPhoneTree = allPhoneTrees.find((tree) => {
        if (tree.onCanvas) {
          // For on-canvas phone trees, the value can be the primitiveId (for saved trees) or the phoneTreeNodeId (for
          // unsaved trees), so we check against both.
          return tree.callObject?.primitiveId === newValue || tree.phoneTreeNodeId === newValue;
        }

        return tree.id === newValue;
      });
      onSelect(selectedPhoneTree);
    }
  };

  const handlePreviewClick = (phoneTree: CanvasPhoneTree | PhoneTree) => {
    setPhoneTreeToPreview(phoneTree);
    previewPhoneTreeTriggerProps.onClick();
  };

  const isLastUsedPhoneTreeUnconfigured =
    !!lastUsedPhoneTree &&
    disableUnconfiguredPhoneTrees &&
    (lastUsedPhoneTree.source === 'callRoute'
      ? getCanvasUnconfiguredReason(lastUsedPhoneTree.tree as CanvasPhoneTree) !== undefined
      : getSavedUnconfiguredReason(lastUsedPhoneTree.tree as PhoneTree) !== undefined);

  return (
    <>
      <DropdownField
        {...dropdownProps}
        onChange={handleChange}
        name={name}
        label={label ?? t('Select Phone Tree')}
        placeholder={placeholder ?? t('Select one')}
        dropdownListStyle={dropdownListStyle}
        {...rest}
      >
        {/* Most Recently Used Phone Tree */}
        {lastUsedPhoneTree && (
          <DropdownField.OptionGroup label={mostRecentlyUsedText}>
            <DropdownField.Option
              key={
                lastUsedPhoneTree.source === 'callRoute'
                  ? lastUsedPhoneTree.tree.callObject?.primitiveId
                  : lastUsedPhoneTree.tree.id
              }
              value={
                lastUsedPhoneTree.source === 'callRoute'
                  ? lastUsedPhoneTree.tree.callObject?.primitiveId
                  : lastUsedPhoneTree.tree.id
              }
              disabled={isLastUsedPhoneTreeUnconfigured}
              displayValue={lastUsedPhoneTree.tree.name}
              css={isLastUsedPhoneTreeUnconfigured ? optionWithTextColorStyle : optionPaddingStyle}
            >
              {lastUsedPhoneTree.source === 'callRoute' ? (
                <CanvasPhoneTreeOption
                  phoneTree={lastUsedPhoneTree.tree as CanvasPhoneTree}
                  unconfiguredReason={
                    isLastUsedPhoneTreeUnconfigured
                      ? getCanvasUnconfiguredReason(lastUsedPhoneTree.tree as CanvasPhoneTree)
                      : undefined
                  }
                  showUnconfiguredTooltip={isLastUsedPhoneTreeUnconfigured}
                  onPreviewClick={handlePreviewClick}
                />
              ) : (
                <PhoneTreeOption
                  phoneTree={lastUsedPhoneTree.tree}
                  unconfiguredReason={
                    isLastUsedPhoneTreeUnconfigured
                      ? getSavedUnconfiguredReason(lastUsedPhoneTree.tree as PhoneTree)
                      : undefined
                  }
                  showUnconfiguredTooltip={isLastUsedPhoneTreeUnconfigured}
                  onPreviewClick={handlePreviewClick}
                />
              )}
            </DropdownField.Option>
          </DropdownField.OptionGroup>
        )}

        {/* Phone Trees in this Call Route */}
        {filteredPhoneTreesInCallRoute && filteredPhoneTreesInCallRoute.length > 0 && (
          <DropdownField.OptionGroup label={t('Phone Trees in this Call Route')}>
            {filteredPhoneTreesInCallRoute.map((phoneTree) => {
              // Prefer primitiveId for on-canvas phone trees since those are for phone tree nodes that have been saved
              // but if the phone tree node hasn't been saved yet, then it won't have a primitiveId and we'll fall back
              // to using the phoneTreeNodeId
              const id = phoneTree.callObject?.primitiveId || phoneTree.phoneTreeNodeId;
              const isUnsavedDisabled = disableUnsavedPhoneTrees && !phoneTree.callObject?.primitiveId;
              const unconfiguredReason = disableUnconfiguredPhoneTrees
                ? getCanvasUnconfiguredReason(phoneTree)
                : undefined;
              const isUnconfigured = !!unconfiguredReason;

              return (
                <DropdownField.Option
                  key={id}
                  value={id}
                  disabled={isUnsavedDisabled || isUnconfigured}
                  displayValue={phoneTree.name}
                  css={optionWithTextColorStyle}
                >
                  <CanvasPhoneTreeOption
                    phoneTree={phoneTree}
                    showUnsavedTreeTooltip={disableUnsavedPhoneTrees}
                    unconfiguredReason={unconfiguredReason}
                    showUnconfiguredTooltip={isUnconfigured}
                    onPreviewClick={handlePreviewClick}
                  />
                </DropdownField.Option>
              );
            })}
          </DropdownField.OptionGroup>
        )}

        {/* Saved Phone Trees grouped by Location */}
        {sortedPhoneTreesByLocation.map(({ locationName, locationId, trees }) => (
          <DropdownField.OptionGroup
            key={locationId}
            label={
              locationId === prioritizedLocationId
                ? isSingleTypeScope
                  ? `${
                      !lastUsedPhoneTree && filteredPhoneTreesInCallRoute && filteredPhoneTreesInCallRoute.length === 0
                        ? // For single locations, If there are no phone trees in the call route and no most recently used,
                          // we don't want to show "Other Phone Trees"
                          // NOTE: Not sure why, but we need at least a space here since when I used an empty string,
                          // then selecting an option, would cause the field to only show the id and not the
                          // displayValue of that option ¯\_(ツ)_/¯
                          ' '
                        : t('Other Phone Trees')
                    }`
                  : `${locationName} (${t('This Location')})`
                : locationName
            }
          >
            {trees.map((phoneTree) => {
              const unconfiguredReason = disableUnconfiguredPhoneTrees
                ? getSavedUnconfiguredReason(phoneTree)
                : undefined;
              const isUnconfigured = !!unconfiguredReason;

              return (
                <DropdownField.Option
                  key={phoneTree.id}
                  value={phoneTree.id}
                  disabled={isUnconfigured}
                  displayValue={phoneTree.name}
                  css={isUnconfigured ? optionWithTextColorStyle : optionPaddingStyle}
                >
                  <PhoneTreeOption
                    phoneTree={phoneTree}
                    unconfiguredReason={unconfiguredReason}
                    showUnconfiguredTooltip={isUnconfigured}
                    onPreviewClick={handlePreviewClick}
                  />
                </DropdownField.Option>
              );
            })}
          </DropdownField.OptionGroup>
        ))}
      </DropdownField>

      {phoneTreeToPreview && (
        <Modal {...previewPhoneTreeModalProps} minWidth={800} maxWidth={800}>
          {'phoneTreeNodeId' in phoneTreeToPreview ? (
            // Canvas Phone Tree Preview
            <PhoneTreePreviewModal
              name={phoneTreeToPreview.name}
              onClose={() => {
                previewPhoneTreeModalProps.onClose();
              }}
              audioFiles={audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }}
              callRouteName={currentCallRouteName}
              callRouteLocationId={currentCallRouteLocationId}
              precomputedData={{
                dialOptions: transformGraphToDialOptions({
                  nodes: phoneTreeToPreview.nodes,
                  edges: phoneTreeToPreview.edges,
                  phoneTreeNodeId: phoneTreeToPreview.phoneTreeNodeId,
                  audioFiles: audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] },
                }),
                extensionDialingEnabled: phoneTreeToPreview.extensionDialingEnabled,
                promptMediaItemId: phoneTreeToPreview.promptMediaItemId,
              }}
            />
          ) : (
            // Saved Phone Tree (from API) Preview
            <PhoneTreePreviewModal
              phoneTreeId={phoneTreeToPreview.id}
              name={phoneTreeToPreview.name ?? ''}
              onClose={previewPhoneTreeModalProps.onClose}
              audioFiles={audioFiles ?? { customMedia: [], standardMedia: [], mailboxGreetings: [], allMedia: [] }}
              callRouteName={phoneTreeToPreview.callRouteData?.name}
              callRouteLocationId={phoneTreeToPreview.callRouteData?.locationId}
            />
          )}
        </Modal>
      )}
    </>
  );
};

// Component used to render a dropdown option for a phone tree that is NOT in the call route canvas currently being viewed.
const PhoneTreeOption = memo(
  ({
    phoneTree,
    unconfiguredReason,
    showUnconfiguredTooltip = false,
    onPreviewClick,
  }: {
    phoneTree: PhoneTree;
    unconfiguredReason?: UnconfiguredReason;
    showUnconfiguredTooltip?: boolean;
    onPreviewClick: (tree: PhoneTree) => void;
  }) => {
    return (
      <PhoneTreeOptionRenderer
        name={phoneTree.name ?? ''}
        subsequentPhoneTreeCount={phoneTree.subsequentPhoneTreeCount ?? 0}
        unconfiguredReason={unconfiguredReason}
        showUnconfiguredTooltip={showUnconfiguredTooltip}
        onViewClick={() => onPreviewClick(phoneTree)}
      />
    );
  }
);
PhoneTreeOption.displayName = 'PhoneTreeOption';

// Component used to render a dropdown option for a phone tree that is in the call route canvas currently being viewed.
export const CanvasPhoneTreeOption = memo(
  ({
    phoneTree,
    showUnsavedTreeTooltip = false,
    unconfiguredReason,
    showUnconfiguredTooltip = false,
    onPreviewClick,
  }: {
    phoneTree: CanvasPhoneTree;
    showUnsavedTreeTooltip?: boolean;
    unconfiguredReason?: UnconfiguredReason;
    showUnconfiguredTooltip?: boolean;
    onPreviewClick: (tree: CanvasPhoneTree) => void;
  }) => {
    const isUnsaved = !phoneTree.callObject?.primitiveId;

    return (
      <PhoneTreeOptionRenderer
        name={phoneTree.name}
        subsequentPhoneTreeCount={phoneTree.subsequentPhoneTreeCount ?? 0}
        isUnsaved={isUnsaved}
        showUnsavedTreeTooltip={showUnsavedTreeTooltip}
        unconfiguredReason={unconfiguredReason}
        showUnconfiguredTooltip={showUnconfiguredTooltip}
        onViewClick={() => onPreviewClick(phoneTree)}
      />
    );
  }
);
CanvasPhoneTreeOption.displayName = 'CanvasPhoneTreeOption';

const dropdownListStyle = css`
  z-index: ${theme.zIndex.modals} !important;
`;

const optionPaddingStyle = css`
  padding-left: ${theme.spacing(1)} !important;
`;

const optionWithTextColorStyle = css`
  padding-left: ${theme.spacing(1)} !important;

  .select-option-text {
    /* 
      We want to show the regular font color instead of the disabled color. We will continue to show
      the disabled icon when hovering plus we will show tooltip.
    */
    color: ${theme.font.colors.default};
  }
`;
